import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid, Box, Button, TextField, InputAdornment, Typography,
  Chip, IconButton, Tooltip, CircularProgress, Checkbox, Collapse,
  Paper, Menu, MenuItem as MuiMenuItem, alpha, Dialog
} from "@mui/material";
import PremiumPage from "../../../Components/layout/PremiumPage";
import { NAVY, NAVY_DARK, GOLD, BORDER, TEXT_SECONDARY } from "../../../theme/tokens";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InventoryIcon from "@mui/icons-material/Inventory";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import dayjs from "dayjs";
import logoBlack from "../../../Images/logo_main_black.webp";
import isBetween from "dayjs/plugin/isBetween";
import { useCreateShopOrderMutation } from "../../../Api/attoDeskApi";
dayjs.extend(isBetween);

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface AdminOrder {
  id: number; shop_id: number; shop_name: string;
  items: { product_id: number; name: string; qty: number; unit?: string; price: number; subtotal: number }[];
  total: number; status: string; notes?: string; portal_note?: string;
  delivery_date?: string; route_name?: string; order_date: string; created_at: string; updated_at: string;
}

const STATUS_MAP: Record<string, { label: string; bg: string; color: string }> = {
  pending:    { label: "Submitted",    bg: "#FFF3E0", color: "#E65100" },
  processing: { label: "Under Review", bg: "#F3E5F5", color: "#6A1B9A" },
  approved:   { label: "Approved",     bg: "#ECFDF5", color: "#047857" },
  confirmed:  { label: "Confirmed",    bg: "#E3F2FD", color: "#0D47A1" },
  rejected:   { label: "Rejected",     bg: "#FEF2F2", color: "#991B1B" },
  delivered:  { label: "Delivered",    bg: "#E8F5E9", color: "#1B5E20" },
  cancelled:  { label: "Cancelled",    bg: "#FFEBEE", color: "#B71C1C" },
};

const STATUS_TABS = [
  { key: "all",        label: "All" },
  { key: "pending",    label: "Submitted" },
  { key: "processing", label: "Under Review" },
  { key: "approved",   label: "Approved" },
  { key: "confirmed",  label: "Confirmed" },
  { key: "rejected",   label: "Rejected" },
  { key: "delivered",  label: "Delivered" },
  { key: "cancelled",  label: "Cancelled" },
  { key: "overdue",    label: "Overdue" },
];

const DATE_FILTERS = [
  { key: "all", label: "All Dates" },
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "week", label: "This Week" },
];

const ASSIGN_FILTERS = [
  { key: "all", label: "All" },
  { key: "assigned", label: "Assigned" },
  { key: "unassigned", label: "Unassigned" },
];

type SortKey = "id" | "shop_name" | "created_at" | "delivery_date" | "total" | "status";

const ShopOrderList: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [allOrders, setAllOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [createShopOrder] = useCreateShopOrderMutation();
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [assignFilter, setAssignFilter] = useState("all");
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [bulkAnchor, setBulkAnchor] = useState<null | HTMLElement>(null);

  const token = localStorage.getItem("token") || "";
  const headers = { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${token}` };

// ── New Order Modal state ──
const [newOrderOpen, setNewOrderOpen] = useState(false);
const [newOrderStep, setNewOrderStep] = useState<1 | 2>(1);
const [newOrderShopSearch, setNewOrderShopSearch] = useState("");
const [newOrderSelectedShop, setNewOrderSelectedShop] = useState<{ id: number; name: string } | null>(null);
const [newOrderShops, setNewOrderShops] = useState<{ id: number; name: string; products: any[] }[]>([]);
const [newOrderShopsLoading, setNewOrderShopsLoading] = useState(false);
const [newOrderCart, setNewOrderCart] = useState<Record<number, number>>({});
const [newOrderNotes, setNewOrderNotes] = useState("");
const [newOrderSubmitting, setNewOrderSubmitting] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      const body: any = {};
      if (search.trim()) body.search = search.trim();
      const res = await fetch(`${API_URL}/shop-orders/all`, { method: "POST", headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) setAllOrders(data.data || []);
    } catch { showErrorMessage("Failed to load orders"); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

const loadShopsForNewOrder = useCallback(() => {
  setNewOrderShopsLoading(true);
  try {
    const shopMap = new Map<number, { id: number; name: string; products: any[] }>();
    allOrders.forEach((o) => {
      if (!shopMap.has(o.shop_id)) {
        shopMap.set(o.shop_id, { id: o.shop_id, name: o.shop_name, products: [] });
      }
    });
    const list = Array.from(shopMap.values()).filter((s) =>
      s.name.toLowerCase().includes(newOrderShopSearch.toLowerCase())
    );
    setNewOrderShops(list);
  } finally {
    setNewOrderShopsLoading(false);
  }
}, [allOrders, newOrderShopSearch]);

// Replace the old useEffect for loadShopsForNewOrder with:
useEffect(() => {
  if (newOrderOpen && newOrderStep === 1) loadShopsForNewOrder();
}, [newOrderOpen, newOrderStep, newOrderShopSearch, allOrders]);

// Load products for the selected shop when moving to step 2
const [newOrderProducts, setNewOrderProducts] = useState<any[]>([]);
const [newOrderProductsLoading, setNewOrderProductsLoading] = useState(false);

const loadProductsForShop = useCallback(async (shopId: number) => {
  setNewOrderProductsLoading(true);
  try {
    // Get all past order items for this shop to build product list
    const shopOrders = allOrders.filter((o) => o.shop_id === shopId);
    const productMap = new Map<number, any>();
    shopOrders.forEach((o) => {
      o.items.forEach((item) => {
        if (!productMap.has(item.product_id)) {
          productMap.set(item.product_id, {
            product_id: item.product_id,
            productName: item.name,
            unit: item.unit || "",
            wholesaleprice: item.price,
            custom_price: null,
          });
        }
      });
    });
    const products = Array.from(productMap.values());
    console.log("Products from order history:", products);
    setNewOrderProducts(products);
  } finally {
    setNewOrderProductsLoading(false);
  }
}, [allOrders]);

const handleNewOrderNext = () => {
  if (!newOrderSelectedShop) return;
  setNewOrderStep(2);
  loadProductsForShop(newOrderSelectedShop.id);
};

const handleNewOrderSubmit = async () => {
  if (!newOrderSelectedShop) return;
  const items = newOrderProducts
    .filter((p) => (newOrderCart[p.product_id ?? p.id] || 0) > 0)
    .map((p) => {
      const pid = p.product_id ?? p.id;
      const price = p.custom_price != null
        ? Number(p.custom_price)
        : Number(p.wholesaleprice ?? p.productPrice ?? 0);
      const qty = newOrderCart[pid];
      return { product_id: pid, name: p.productName || p.name, qty, price, subtotal: price * qty };
    });
  if (items.length === 0) return;
  const total = items.reduce((s, i) => s + i.subtotal, 0);
  setNewOrderSubmitting(true);
  try {
    await createShopOrder({
      shopId: newOrderSelectedShop.id,
      items,
      total,
      notes: newOrderNotes,
    }).unwrap();
    showMessage("Order created successfully");
    setNewOrderOpen(false);
    setNewOrderStep(1);
    setNewOrderSelectedShop(null);
    setNewOrderCart({});
    setNewOrderNotes("");
    loadOrders();
  } catch (e: any) {
    showErrorMessage(e?.data?.message || "Failed to create order");
  } finally {
    setNewOrderSubmitting(false);
  }
};

  const handleStatusChange = async (orderId: number, newStatus: string) => {
    setUpdatingIds(prev => new Set(prev).add(orderId));
    try {
      const res = await fetch(`${API_URL}/shop-orders/${orderId}/status`, { method: "PUT", headers, body: JSON.stringify({ status: newStatus }) });
      const data = await res.json();
      if (data.success) { showMessage(`Order #${orderId} ${newStatus}`); if (data.route_warning) showErrorMessage(data.route_warning); loadOrders(); }
      else showErrorMessage(data.message || "Failed");
    } catch { showErrorMessage("Network error"); }
    finally { setUpdatingIds(prev => { const n = new Set(prev); n.delete(orderId); return n; }); }
  };

  const handleBulkAction = async (action: string) => {
    setBulkAnchor(null);
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    for (const id of ids) { await handleStatusChange(id, action); }
    setSelected(new Set());
  };

  // Filtering
  const filteredOrders = useMemo(() => {
    let list = allOrders;
    if (statusFilter === "overdue") {
      list = list.filter(o => o.status === "pending" && o.delivery_date && dayjs(o.delivery_date).isBefore(dayjs(), "day"));
    } else if (statusFilter !== "all") {
      list = list.filter(o => o.status === statusFilter);
    }
    if (dateFilter === "today") {
      const today = dayjs().format("YYYY-MM-DD");
      list = list.filter(o => o.delivery_date === today);
    } else if (dateFilter === "tomorrow") {
      const tom = dayjs().add(1, "day").format("YYYY-MM-DD");
      list = list.filter(o => o.delivery_date === tom);
    } else if (dateFilter === "week") {
      const start = dayjs().startOf("week"); const end = dayjs().endOf("week");
      list = list.filter(o => o.delivery_date && dayjs(o.delivery_date).isBetween(start, end, "day", "[]"));
    }
    if (assignFilter === "assigned") list = list.filter(o => !!o.route_name);
    else if (assignFilter === "unassigned") list = list.filter(o => !o.route_name);
    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((o: any) =>
        String(o.id).includes(q) ||
        (o.shop_name || "").toLowerCase().includes(q) ||
        (o.contact_name || "").toLowerCase().includes(q) ||
        (o.route_name || "").toLowerCase().includes(q) ||
        (o.status || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [allOrders, statusFilter, dateFilter, assignFilter, debouncedSearch]);

  // Sorting
  const sortedOrders = useMemo(() => {
    const sorted = [...filteredOrders].sort((a, b) => {
      let va: any, vb: any;
      switch (sortKey) {
        case "id": va = a.id; vb = b.id; break;
        case "shop_name": va = a.shop_name.toLowerCase(); vb = b.shop_name.toLowerCase(); break;
        case "created_at": va = a.created_at; vb = b.created_at; break;
        case "delivery_date": va = a.delivery_date || ""; vb = b.delivery_date || ""; break;
        case "total": va = a.total; vb = b.total; break;
        case "status": va = a.status; vb = b.status; break;
        default: va = a.id; vb = b.id;
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredOrders, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return null;
    return sortDir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 12, ml: 0.25 }} /> : <ArrowDownwardIcon sx={{ fontSize: 12, ml: 0.25 }} />;
  };

  // Counts
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: allOrders.length };
    const today = dayjs();
    allOrders.forEach(o => {
      c[o.status] = (c[o.status] || 0) + 1;
      if (o.status === "pending" && o.delivery_date && dayjs(o.delivery_date).isBefore(today, "day")) {
        c.overdue = (c.overdue || 0) + 1;
      }
    });
    return c;
  }, [allOrders]);

  const isOverdue = (o: AdminOrder) => o.status === "pending" && o.delivery_date && dayjs(o.delivery_date).isBefore(dayjs(), "day");
  const isUpdating = (id: number) => updatingIds.has(id);

  const toggleSelect = (id: number) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => {
    if (selected.size === sortedOrders.length) setSelected(new Set());
    else setSelected(new Set(sortedOrders.map(o => o.id)));
  };

  const getRowBg = (o: AdminOrder) => {
    if (isOverdue(o)) return "#FFF8F6";
    if (o.status === "pending") return "#FFFDF5";
    return undefined;
  };

  const handlePrintList = () => {
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) return;
    const logoUrl = (logoBlack as string).startsWith("http") || (logoBlack as string).startsWith("data:")
      ? (logoBlack as string)
      : `${window.location.origin}${logoBlack}`;
    const reportDate = dayjs().format("MMMM DD, YYYY");
    const preparedBy = localStorage.getItem("user_email") || localStorage.getItem("email") || "—";
    const filterParts: string[] = [];
    if (statusFilter !== "all") filterParts.push(`Status: ${statusFilter}`);
    if (dateFilter !== "all") filterParts.push(`Date: ${dateFilter}`);
    if (assignFilter !== "all") filterParts.push(`Assignment: ${assignFilter}`);
    const filterLabel = filterParts.length ? filterParts.join(" • ") : "All Orders";

    const overdueCount = sortedOrders.filter(isOverdue).length;
    const totalAmount = sortedOrders.reduce((s, o: any) => s + Number(o.total || 0), 0);

    const statusPill = (s: string) => {
      const k = (s || "").toLowerCase();
      const cls = ["delivered","confirmed","paid"].includes(k) ? "ok"
        : ["cancelled","unpaid"].includes(k) ? "danger"
        : "warn";
      const label = s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
      return `<span class="status ${cls}">${label}</span>`;
    };

    const rowsHtml = sortedOrders.map((o: any, i: number) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td class="invno"><div class="invno-main">#${o.id}</div></td>
        <td class="client">${o.shop_name || `Shop #${o.shop_id}`}</td>
        <td>${dayjs(o.created_at).format("MMM D, YYYY")}</td>
        <td>${o.delivery_date ? dayjs(o.delivery_date).format("MMM D, YYYY") : "—"}${o.route_name ? `<div style="font-size:11px;color:#64748b">${o.route_name}</div>` : ""}</td>
        <td class="right">${o.items_count ?? (Array.isArray(o.items) ? o.items.length : "—")}</td>
        <td class="right amt">$${Number(o.total || 0).toFixed(2)}</td>
        <td>${statusPill(o.status)}</td>
      </tr>
    `).join("");

    w.document.write(`<!DOCTYPE html><html><head><title>Mr. Veggie Ltd Orders Report</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1f2937;background:#fff;padding:36px 40px}
  .report{max-width:1180px;margin:0 auto}
  .report-header{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:18px}
  .header-left .logo{height:46px;width:auto;display:block;margin-bottom:10px}
  .header-left .meta{font-size:12px;color:#475569;line-height:1.7}
  .header-right{text-align:right}
  .header-right .title{font-size:26px;font-weight:800;color:#0B1F3A;letter-spacing:.6px;font-family:Georgia,'Times New Roman',serif}
  .header-right .meta{font-size:12px;color:#475569;line-height:1.7;margin-top:4px}
  .filter-strip{background:#eff4fb;border-top:1px solid #d6e1ef;border-bottom:1px solid #d6e1ef;padding:10px 14px;font-size:12px;color:#0B1F3A;margin-bottom:18px}
  .filter-strip strong{font-weight:700;margin-right:6px}
  .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:18px}
  .sum-card{border:1px solid;padding:18px 20px;border-radius:4px;text-align:center}
  .sum-card .label{font-size:10.5px;font-weight:700;letter-spacing:1.2px;color:#64748b;margin-bottom:8px}
  .sum-card .value{font-size:22px;font-weight:800;font-family:Georgia,'Times New Roman',serif}
  .sum-card.total{background:#f3f6fb;border-color:#dde5f0}.sum-card.total .value{color:#0B1F3A}
  .sum-card.pending{background:#fef6e7;border-color:#f4dca0}.sum-card.pending .value{color:#b45309}
  .sum-card.delivered{background:#eafaf0;border-color:#c5ecd2}.sum-card.delivered .value{color:#15803d}
  .sum-card.overdue{background:#fdecec;border-color:#f6c2c2}.sum-card.overdue .value{color:#b91c1c}
  table{width:100%;border-collapse:collapse;font-size:12.5px}
  thead th{background:#0B1F3A;color:#fff;font-size:10.5px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;padding:12px 12px;text-align:left;border-bottom:1px solid #0B1F3A}
  thead th.right{text-align:right}
  tbody td{padding:14px 12px;border-bottom:1px solid #e5e7eb;vertical-align:middle}
  tbody td.num{color:#94a3b8;font-size:11px;width:36px}
  tbody td.right{text-align:right}
  .invno-main{font-weight:700;color:#0B1F3A}
  tbody td.client{font-weight:600;color:#1f2937}
  tbody td.amt{font-weight:700;color:#1f2937}
  .status{display:inline-block;padding:3px 10px;border-radius:4px;font-size:11px;font-weight:700;text-transform:capitalize}
  .status.ok{color:#15803d}
  .status.warn{color:#b45309}
  .status.danger{color:#b91c1c}
  tfoot td{background:#0B1F3A;color:#fff;font-size:12.5px;font-weight:700;padding:14px 12px;letter-spacing:.4px}
  tfoot td.right{text-align:right}
  .divider{height:1px;background:#e5e7eb;margin:28px 0 14px 0}
  .footer{text-align:center;font-size:11px;color:#64748b;padding-top:6px}
  .footer .brand{color:#0B1F3A;font-weight:700}
  @media print{body{padding:18px}}
</style>
</head><body>
<div class="report">
  <div class="report-header">
    <div class="header-left">
      <img src="${logoUrl}" alt="Mr. Veggie Ltd" class="logo" />
      <div class="meta">
        Mr. Veggie Ltd — Fresh From The Earth<br/>
        support@cacart.com<br/>
      </div>
    </div>
    <div class="header-right">
      <div class="title">ORDERS REPORT</div>
      <div class="meta">
        Date: ${reportDate}<br/>
        Prepared By: ${preparedBy}
      </div>
    </div>
  </div>
  <div class="filter-strip"><strong>Filter:</strong>${filterLabel}</div>
  <div class="summary">
    <div class="sum-card total"><div class="label">TOTAL ORDERS</div><div class="value">${sortedOrders.length}</div></div>
    <div class="sum-card pending"><div class="label">PENDING</div><div class="value">${counts.pending || 0}</div></div>
    <div class="sum-card delivered"><div class="label">DELIVERED</div><div class="value">${counts.delivered || 0}</div></div>
    <div class="sum-card overdue"><div class="label">OVERDUE</div><div class="value">${overdueCount}</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Order #</th>
        <th>Restaurant</th>
        <th>Date</th>
        <th>Delivery</th>
        <th class="right">Items</th>
        <th class="right">Total</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>${rowsHtml || `<tr><td colspan="8" style="text-align:center;padding:24px;color:#94a3b8">No orders</td></tr>`}</tbody>
    <tfoot>
      <tr>
        <td colspan="6">TOTALS &nbsp;|&nbsp; ${sortedOrders.length} order${sortedOrders.length === 1 ? "" : "s"}</td>
        <td class="right">$${totalAmount.toFixed(2)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
  <div class="divider"></div>
  <div class="footer">
    <div><span class="brand">Mr. Veggie Ltd</span> — Smart Inventory & Order Management</div>
    <div>Generated via Mr. Veggie Ltd System &nbsp;•&nbsp; © ${dayjs().format("YYYY")}</div>
  </div>
</div>
<script>window.onload=function(){window.print()}</script>
</body></html>`);
    w.document.close();
  };

  // Per-row print: same blade as handlePrintList, scoped to a single order's items
  const handlePrintOrder = (o: AdminOrder) => {
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) return;
    const logoUrl = (logoBlack as string).startsWith("http") || (logoBlack as string).startsWith("data:")
      ? (logoBlack as string)
      : `${window.location.origin}${logoBlack}`;
    const reportDate = dayjs().format("MMMM DD, YYYY");
    const preparedBy = localStorage.getItem("user_email") || localStorage.getItem("email") || "—";
    const sm = STATUS_MAP[o.status] || { label: o.status, bg: "#f5f5f5", color: "#666" };
    const totalQty = (o.items || []).reduce((s, it: any) => s + Number(it.qty || 0), 0);
    const itemsHtml = (o.items || []).map((it: any, i: number) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td class="client">${it.name || ""}</td>
        <td class="right">${Number(it.qty || 0)}</td>
        <td>${it.unit || "—"}</td>
        <td class="right">$${Number(it.price || 0).toFixed(2)}</td>
        <td class="right amt">$${(Number(it.qty || 0) * Number(it.price || 0)).toFixed(2)}</td>
      </tr>
    `).join("") || `<tr><td colspan="6" style="text-align:center;padding:24px;color:#94a3b8">No items</td></tr>`;

    w.document.write(`<!DOCTYPE html><html><head><title>Mr. Veggie Ltd Order #${o.id}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1f2937;background:#fff;padding:36px 40px}
  .report{max-width:1180px;margin:0 auto}
  .report-header{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:18px}
  .header-left .logo{height:46px;width:auto;display:block;margin-bottom:10px}
  .header-left .meta{font-size:12px;color:#475569;line-height:1.7}
  .header-right{text-align:right}
  .header-right .title{font-size:26px;font-weight:800;color:#0B1F3A;letter-spacing:.6px;font-family:Georgia,'Times New Roman',serif}
  .header-right .meta{font-size:12px;color:#475569;line-height:1.7;margin-top:4px}
  .filter-strip{background:#eff4fb;border-top:1px solid #d6e1ef;border-bottom:1px solid #d6e1ef;padding:10px 14px;font-size:12px;color:#0B1F3A;margin-bottom:18px}
  .filter-strip strong{font-weight:700;margin-right:6px}
  .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:18px}
  .sum-card{border:1px solid;padding:18px 20px;border-radius:4px;text-align:center}
  .sum-card .label{font-size:10.5px;font-weight:700;letter-spacing:1.2px;color:#64748b;margin-bottom:8px}
  .sum-card .value{font-size:22px;font-weight:800;font-family:Georgia,'Times New Roman',serif}
  .sum-card.total{background:#f3f6fb;border-color:#dde5f0}.sum-card.total .value{color:#0B1F3A}
  .sum-card.pending{background:#fef6e7;border-color:#f4dca0}.sum-card.pending .value{color:#b45309}
  .sum-card.delivered{background:#eafaf0;border-color:#c5ecd2}.sum-card.delivered .value{color:#15803d}
  .sum-card.overdue{background:#fdecec;border-color:#f6c2c2}.sum-card.overdue .value{color:#b91c1c}
  table{width:100%;border-collapse:collapse;font-size:12.5px}
  thead th{background:#0B1F3A;color:#fff;font-size:10.5px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;padding:12px 12px;text-align:left;border-bottom:1px solid #0B1F3A}
  thead th.right{text-align:right}
  tbody td{padding:14px 12px;border-bottom:1px solid #e5e7eb;vertical-align:middle}
  tbody td.num{color:#94a3b8;font-size:11px;width:36px}
  tbody td.right{text-align:right}
  tbody td.client{font-weight:600;color:#1f2937}
  tbody td.amt{font-weight:700;color:#1f2937}
  tfoot td{background:#0B1F3A;color:#fff;font-size:12.5px;font-weight:700;padding:14px 12px;letter-spacing:.4px}
  tfoot td.right{text-align:right}
  .divider{height:1px;background:#e5e7eb;margin:28px 0 14px 0}
  .footer{text-align:center;font-size:11px;color:#64748b;padding-top:6px}
  .footer .brand{color:#0B1F3A;font-weight:700}
  @media print{body{padding:18px}}
</style></head><body>
<div class="report">
  <div class="report-header">
    <div class="header-left">
      <img src="${logoUrl}" alt="Mr. Veggie Ltd" class="logo" />
      <div class="meta">Mr. Veggie Ltd — Fresh From The Earth<br/>support@cacart.com</div>
    </div>
    <div class="header-right">
      <div class="title">ORDER REPORT</div>
      <div class="meta">Date: ${reportDate}<br/>Prepared By: ${preparedBy}</div>
    </div>
  </div>
  <div class="filter-strip">
    <strong>Restaurant:</strong> ${o.shop_name || `Shop #${o.shop_id}`} &nbsp;•&nbsp;
    <strong>Order #:</strong> ${o.id} &nbsp;•&nbsp;
    <strong>Created:</strong> ${dayjs(o.order_date || o.created_at).format("MMM D, YYYY")} &nbsp;•&nbsp;
    <strong>Delivery:</strong> ${o.delivery_date ? dayjs(o.delivery_date).format("MMM D, YYYY") : "—"}
    ${o.route_name ? ` &nbsp;•&nbsp; <strong>Route:</strong> ${o.route_name}` : ""} &nbsp;•&nbsp;
    <strong>Status:</strong> ${sm.label}
  </div>
  <div class="summary">
    <div class="sum-card total"><div class="label">ORDER #</div><div class="value">#${o.id}</div></div>
    <div class="sum-card pending"><div class="label">ITEMS</div><div class="value">${(o.items || []).length}</div></div>
    <div class="sum-card delivered"><div class="label">QTY</div><div class="value">${totalQty}</div></div>
    <div class="sum-card overdue"><div class="label">TOTAL</div><div class="value">$${Number(o.total || 0).toFixed(2)}</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th><th>Product</th><th class="right">Qty</th><th>Unit</th><th class="right">Price</th><th class="right">Subtotal</th>
      </tr>
    </thead>
    <tbody>${itemsHtml}</tbody>
    <tfoot>
      <tr><td colspan="5">TOTALS &nbsp;|&nbsp; ${(o.items || []).length} item${(o.items || []).length === 1 ? "" : "s"} · ${totalQty} units</td><td class="right">$${Number(o.total || 0).toFixed(2)}</td></tr>
    </tfoot>
  </table>
  ${o.notes || o.portal_note ? `<div class="divider"></div><div style="font-size:12px;color:#475569;line-height:1.7"><strong style="color:#0B1F3A">Notes:</strong> ${(o.notes || o.portal_note || "").toString().replace(/[<>]/g, "")}</div>` : ""}
  <div class="divider"></div>
  <div class="footer">
    <div><span class="brand">Mr. Veggie Ltd</span> — Smart Inventory & Order Management</div>
    <div>Generated via Mr. Veggie Ltd System &nbsp;•&nbsp; © ${dayjs().format("YYYY")}</div>
  </div>
</div>
<script>window.onload=function(){window.print()}</script>
</body></html>`);
    w.document.close();
  };

  return (
    <PremiumPage disablePaper>
      <Grid container>
        {/* ── Header ── */}
        <Grid item xs={12} className={classes.titleGrid} sx={{ px: 3, py: 2 }}>
          <style>{`
            @media print {
              .no-print, .sidebar, header, nav, .MuiDrawer-root, .filters, .action-buttons { display: none !important; }
              body { background: #fff !important; }
              .print-only { display: block !important; }
              table { font-size: 12px; border-collapse: collapse; width: 100%; }
              th, td { padding: 8px; border: 1px solid #ddd !important; }
              .MuiCard-root, .MuiPaper-root { box-shadow: none !important; }
            }
            .print-only { display: none; }
          `}</style>
          <Box className="print-only" sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0B1F3A" }}>Mr. Veggie Ltd – Orders List</Typography>
            <Typography sx={{ fontSize: 11, color: "#64748b" }}>Generated {dayjs().format("MMM D, YYYY h:mm A")}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }} className="no-print">
            <Typography className={classes.cardTitle}>Orders</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField size="small" placeholder="Search restaurant or order #..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 18, color: "#999" }} /></InputAdornment>, classes: { root: classes.formControlList } }}
                sx={{ width: 280, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              <Button
                variant="contained"
                disableElevation
                startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                onClick={handlePrintList}
                sx={{
                  bgcolor: "#0B1F3A", color: "#fff", height: 36, borderRadius: "8px",
                  textTransform: "none", fontWeight: 600, fontSize: 13, px: 2,
                  "&:hover": { bgcolor: "#0F2A4A" },
                }}
              >
                Print List
              </Button>
                <Button
    variant="contained" disableElevation
    startIcon={<span style={{ fontSize: 16, lineHeight: 1 }}>+</span>}
    onClick={() => { setNewOrderOpen(true); setNewOrderStep(1); setNewOrderSelectedShop(null); setNewOrderCart({}); setNewOrderNotes(""); }}
    sx={{ bgcolor: "#2e7d32", color: "#fff", height: 36, borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 13, px: 2, "&:hover": { bgcolor: "#1b5e20" } }}
  >
    New Order
  </Button>
            </Box>
          </Box>

          {/* ── Summary Bar ── */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            {[
              { icon: <InventoryIcon sx={{ fontSize: 16, color: "#1565C0" }} />, bg: "#E3F2FD", label: "Total Orders", value: allOrders.length },
              { icon: <PendingActionsIcon sx={{ fontSize: 16, color: "#E65100" }} />, bg: "#FFF3E0", label: "Pending", value: counts.pending || 0 },
              { icon: <DoneAllIcon sx={{ fontSize: 16, color: "#1B5E20" }} />, bg: "#E8F5E9", label: "Delivered", value: counts.delivered || 0 },
              { icon: <WarningAmberIcon sx={{ fontSize: 16, color: "#C62828" }} />, bg: "#FFEBEE", label: "Overdue", value: allOrders.filter(o => isOverdue(o)).length },
            ].map(s => (
              <Paper key={s.label} elevation={0} sx={{ px: 2, py: 1, borderRadius: 2, border: "1px solid #eee", display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: s.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.icon}</Box>
                <Box><Typography fontSize={10} color="#999" fontWeight={600} textTransform="uppercase">{s.label}</Typography><Typography fontSize={15} fontWeight={800}>{s.value}</Typography></Box>
              </Paper>
            ))}
          </Box>

          {/* ── Filters Row ── */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            {/* Status tabs */}
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {STATUS_TABS.map(t => {
                const count = t.key === "all" ? allOrders.length : (counts[t.key] || 0);
                const active = statusFilter === t.key;
                return (
                  <Chip key={t.key} label={`${t.label} (${count})`} onClick={() => setStatusFilter(t.key)} size="small"
                    sx={{ fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: "8px", height: 28,
                      ...(active ? { bgcolor: NAVY, color: GOLD, border: `1px solid ${alpha(GOLD, 0.4)}` } : { bgcolor: "#f5f5f5", color: "#666", "&:hover": { bgcolor: alpha(GOLD, 0.08), color: NAVY } }) }} />
                );
              })}
            </Box>

            <Box sx={{ width: 1, height: 24, bgcolor: "#e0e0e0" }} />

            {/* Date filter */}
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {DATE_FILTERS.map(t => {
                const active = dateFilter === t.key;
                return (
                  <Chip key={t.key} label={t.label} onClick={() => setDateFilter(t.key)} size="small"
                    sx={{ fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: "8px", height: 28,
                      ...(active ? { bgcolor: "#1565C0", color: "#fff" } : { bgcolor: "#f5f5f5", color: "#666", "&:hover": { bgcolor: "#e0e0e0" } }) }} />
                );
              })}
            </Box>

            <Box sx={{ width: 1, height: 24, bgcolor: "#e0e0e0" }} />

            {/* Assignment filter */}
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {ASSIGN_FILTERS.map(t => {
                const active = assignFilter === t.key;
                return (
                  <Chip key={t.key} label={t.label} onClick={() => setAssignFilter(t.key)} size="small"
                    sx={{ fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: "8px", height: 28,
                      ...(active ? { bgcolor: "#7B1FA2", color: "#fff" } : { bgcolor: "#f5f5f5", color: "#666", "&:hover": { bgcolor: "#e0e0e0" } }) }} />
                );
              })}
            </Box>
          </Box>

          {/* ── Bulk Actions ── */}
          {selected.size > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, p: 1.5, bgcolor: alpha(NAVY, 0.04), borderRadius: 2, border: `1px solid ${alpha(GOLD, 0.25)}` }}>
              <Typography fontSize={13} fontWeight={700} color={NAVY}>{selected.size} selected</Typography>
              <Button size="small" variant="contained" onClick={(e) => setBulkAnchor(e.currentTarget)}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: NAVY, color: GOLD, "&:hover": { bgcolor: NAVY_DARK } }}>
                Bulk Actions
              </Button>
              <Button size="small" variant="text" onClick={() => setSelected(new Set())}
                sx={{ textTransform: "none", fontSize: 12, color: "#999" }}>Clear</Button>
              <Menu anchorEl={bulkAnchor} open={!!bulkAnchor} onClose={() => setBulkAnchor(null)}>
                <MuiMenuItem onClick={() => handleBulkAction("confirmed")} sx={{ fontSize: 13 }}>
                  <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: "#2E7D32" }} /> Approve Selected
                </MuiMenuItem>
                <MuiMenuItem onClick={() => handleBulkAction("delivered")} sx={{ fontSize: 13 }}>
                  <LocalShippingIcon sx={{ fontSize: 16, mr: 1, color: "#1565C0" }} /> Mark Delivered
                </MuiMenuItem>
                <MuiMenuItem onClick={() => handleBulkAction("cancelled")} sx={{ fontSize: 13 }}>
                  <CancelIcon sx={{ fontSize: 16, mr: 1, color: "#C62828" }} /> Reject Selected
                </MuiMenuItem>
              </Menu>
            </Box>
          )}
        </Grid>

        {/* ── Table ── */}
        <Grid item xs={12}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={32} sx={{ color: GOLD }} /></Box>
          ) : sortedOrders.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}><Typography fontSize={14} color="text.secondary">No orders found</Typography></Box>
          ) : (
            <Paper elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 6px 20px rgba(0,0,0,0.06)" }}>
            <TableContainer sx={{ scrollBehavior: "smooth" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40, p: 1, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 11, background: `linear-gradient(180deg, ${alpha(NAVY, 0.06)} 0%, #f8fafc 100%)`, borderBottom: `1px solid ${BORDER}` }}>
                      <Checkbox size="small" checked={selected.size === sortedOrders.length && sortedOrders.length > 0}
                        indeterminate={selected.size > 0 && selected.size < sortedOrders.length}
                        onChange={selectAll} sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18, color: alpha(NAVY, 0.35) }, "&.Mui-checked .MuiSvgIcon-root, &.MuiCheckbox-indeterminate .MuiSvgIcon-root": { color: GOLD } }} />
                    </TableCell>
                    {[
                      { key: "id" as SortKey, label: "Order #", w: undefined },
                      { key: "shop_name" as SortKey, label: "Restaurant", w: undefined },
                      { key: "created_at" as SortKey, label: "Date", w: undefined },
                      { key: "delivery_date" as SortKey, label: "Delivery", w: undefined },
                      { key: null, label: "Items", w: undefined },
                      { key: "total" as SortKey, label: "Total", w: undefined },
                      { key: "status" as SortKey, label: "Status", w: undefined },
                    ].map(col => (
                      <TableCell key={col.label}
                        onClick={() => col.key && toggleSort(col.key)}
                        sx={{
                          fontWeight: 800, color: NAVY, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
                          py: 1.5, borderBottom: `1px solid ${BORDER}`,
                          background: `linear-gradient(180deg, ${alpha(NAVY, 0.06)} 0%, #f8fafc 100%)`,
                          cursor: col.key ? "pointer" : "default", userSelect: "none",
                          "&:hover": col.key ? { color: GOLD } : {},
                        }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {col.label}
                          {col.key && <SortIcon k={col.key} />}
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell align="left" sx={{ fontWeight: 800, color: NAVY, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", py: 1.5, minWidth: 200, borderBottom: `1px solid ${BORDER}`, background: `linear-gradient(180deg, ${alpha(NAVY, 0.06)} 0%, #f8fafc 100%)`, textAlign: "left", verticalAlign: "middle" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOrders.map((order) => {
                    const sm = STATUS_MAP[order.status] || { label: order.status, bg: "#f5f5f5", color: "#666" };
                    const overdue = isOverdue(order);
                    const expanded = expandedId === order.id;
                    return (
                      <React.Fragment key={order.id}>
                        <TableRow sx={{
                          bgcolor: getRowBg(order),
                          transition: "background 0.18s ease",
                          "&:hover": { bgcolor: alpha(NAVY, 0.03) },
                          "&:hover .action-buttons": { boxShadow: `0 4px 14px ${alpha(NAVY, 0.10)}`, borderColor: alpha(NAVY, 0.12) },
                        }}>
                          <TableCell className={classes.tablCell} sx={{ p: 1 }}>
                            <Checkbox size="small" checked={selected.has(order.id)} onChange={() => toggleSelect(order.id)}
                              sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18 } }} />
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={700}>#{order.id}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={600}>{order.shop_name}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={12}>{dayjs(order.order_date  || order.created_at).format("MMM D, YYYY")}</Typography>
                            <Typography fontSize={10} color="text.secondary">{dayjs(order.created_at).format("h:mm A")}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            {order.delivery_date ? (
                              <Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Typography fontSize={12} fontWeight={600}>
                                    {dayjs(order.delivery_date).format("ddd, MMM D")}
                                  </Typography>
                                  {overdue && <WarningAmberIcon sx={{ fontSize: 13, color: "#C62828" }} />}
                                </Box>
                                {order.route_name && (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                    <AltRouteIcon sx={{ fontSize: 10, color: "#999" }} />
                                    <Typography fontSize={10} color="text.secondary">{order.route_name}</Typography>
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Chip label="Unassigned" size="small" variant="outlined"
                                sx={{ fontSize: 10, height: 20, borderColor: "#e0e0e0", color: "#bbb" }} />
                            )}
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={12} fontWeight={600}>
                              {order.items.length} item{order.items.length !== 1 ? "s" : ""}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={700}>${order.total.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Chip label={sm.label} size="small"
                                sx={{ fontSize: 10, fontWeight: 700, bgcolor: sm.bg, color: sm.color, height: 22, borderRadius: "11px" }} />
                              {overdue && <Chip label="Overdue" size="small" sx={{ fontSize: 9, fontWeight: 700, bgcolor: "#FFEBEE", color: "#C62828", height: 18, borderRadius: "9px" }} />}
                            </Box>
                          </TableCell>
                          <TableCell className={classes.tablCell} sx={{ verticalAlign: "middle", py: 1, minWidth: 240 }}>
                            {(() => {
                              // Reusable soft-pill icon button
                              const PillBtn = ({ title, icon, onClick, color, disabled }: { title: string; icon: React.ReactNode; onClick: () => void; color: { bg: string; bgHover: string; fg: string }; disabled?: boolean }) => (
                                <Tooltip title={title}>
                                  <span>
                                    <IconButton size="small" disabled={disabled} onClick={onClick}
                                      sx={{
                                        width: 36, height: 36, borderRadius: "10px",
                                        bgcolor: color.bg, color: color.fg,
                                        transition: "all 0.18s ease",
                                        "&:hover": {
                                          bgcolor: color.bgHover,
                                          transform: "translateY(-1px) scale(1.03)",
                                          boxShadow: `0 4px 12px ${alpha(color.fg, 0.18)}`,
                                        },
                                        "&.Mui-disabled": { opacity: 0.4, transform: "none" },
                                      }}>{icon}</IconButton>
                                  </span>
                                </Tooltip>
                              );
                              const PALETTE = {
                                view:    { bg: alpha("#0B1F3A", 0.06), bgHover: alpha("#0B1F3A", 0.12), fg: "#0B1F3A" },
                                print:   { bg: alpha("#4f46e5", 0.08), bgHover: alpha("#4f46e5", 0.16), fg: "#4338ca" },
                                approve: { bg: alpha("#059669", 0.08), bgHover: alpha("#059669", 0.16), fg: "#047857" },
                                reject:  { bg: alpha("#dc2626", 0.08), bgHover: alpha("#dc2626", 0.16), fg: "#b91c1c" },
                                edit:    { bg: alpha("#d97706", 0.08), bgHover: alpha("#d97706", 0.16), fg: "#b45309" },
                                more:    { bg: alpha("#475569", 0.06), bgHover: alpha("#475569", 0.12), fg: "#475569" },
                              };
                              const status = order.status;
                              // Status-based visibility per spec
                              const showApproveReject = status === "pending";
                              const showEdit = ["pending", "processing", "rejected"].includes(status);
                              return (
                                <Box className="action-buttons" sx={{
                                  display: "inline-flex", alignItems: "center", gap: 1,
                                  p: "4px",
                                  borderRadius: "14px",
                                  bgcolor: alpha("#ffffff", 0.6),
                                  backdropFilter: "blur(6px)",
                                  border: `1px solid ${alpha(NAVY, 0.06)}`,
                                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
                                }}>
                                  <PillBtn title="View Order" icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => navigate(`/shop-order/${order.id}`)} color={PALETTE.view} />
                                  <PillBtn title="Print Order" icon={<PrintIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => handlePrintOrder(order)} color={PALETTE.print} />
                                  {showApproveReject && (
                                    <>
                                      <PillBtn title="Approve" icon={isUpdating(order.id) ? <CircularProgress size={14} sx={{ color: PALETTE.approve.fg }} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => handleStatusChange(order.id, "confirmed")} color={PALETTE.approve} disabled={isUpdating(order.id)} />
                                      <PillBtn title="Reject" icon={<CancelIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => handleStatusChange(order.id, "cancelled")} color={PALETTE.reject} disabled={isUpdating(order.id)} />
                                    </>
                                  )}
                                  {showEdit && (
                                    <PillBtn title="Edit Order" icon={<EditIcon sx={{ fontSize: 16 }} />}
                                      onClick={() => navigate(`/shop-order/${order.id}`)} color={PALETTE.edit} />
                                  )}
                                  <PillBtn title={expanded ? "Collapse" : "More"} icon={expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => setExpandedId(expanded ? null : order.id)} color={PALETTE.more} />
                                </Box>
                              );
                            })()}
                          </TableCell>
                        </TableRow>

                        {/* ── Expanded Row ── */}
                        <TableRow>
                          <TableCell colSpan={10} sx={{ py: 0, px: 0, borderBottom: expanded ? "1px solid #e0e0e0" : "none" }}>
                            <Collapse in={expanded} timeout={200}>
                              <Box sx={{ px: 3, py: 2, bgcolor: "#FAFAFA" }}>
                                <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                  <Box sx={{ flex: 1, minWidth: 300 }}>
                                    <Typography fontSize={11} fontWeight={700} color="#999" textTransform="uppercase" mb={1}>Order Items</Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8" }}>Product</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8", width: 60 }}>Qty</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8", width: 50 }}>Unit</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8", width: 70, textAlign: "right" }}>Price</TableCell>
                                          <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 0.5, borderBottom: "1px solid #e8e8e8", width: 80, textAlign: "right" }}>Subtotal</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {order.items.map((it, i) => (
                                          <TableRow key={i}>
                                            <TableCell sx={{ fontSize: 12, py: 0.5, borderBottom: "1px solid #f5f5f5" }}>{it.name}</TableCell>
                                            <TableCell sx={{ fontSize: 12, py: 0.5, borderBottom: "1px solid #f5f5f5" }}>{it.qty}</TableCell>
                                            <TableCell sx={{ fontSize: 11, py: 0.5, borderBottom: "1px solid #f5f5f5", color: "#999" }}>{it.unit || "—"}</TableCell>
                                            <TableCell sx={{ fontSize: 12, py: 0.5, borderBottom: "1px solid #f5f5f5", textAlign: "right" }}>${it.price.toFixed(2)}</TableCell>
                                            <TableCell sx={{ fontSize: 12, py: 0.5, borderBottom: "1px solid #f5f5f5", textAlign: "right", fontWeight: 600 }}>${it.subtotal.toFixed(2)}</TableCell>
                                          </TableRow>
                                        ))}
                                        <TableRow>
                                          <TableCell colSpan={4} sx={{ fontSize: 12, fontWeight: 700, py: 0.75, textAlign: "right", borderBottom: "none" }}>Total</TableCell>
                                          <TableCell sx={{ fontSize: 13, fontWeight: 800, py: 0.75, textAlign: "right", borderBottom: "none", color: NAVY }}>${order.total.toFixed(2)}</TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </Box>
                                  <Box sx={{ minWidth: 200 }}>
                                    <Typography fontSize={11} fontWeight={700} color="#999" textTransform="uppercase" mb={1}>Details</Typography>
                                    {order.portal_note && (
                                      <Box sx={{ mb: 1, p: 1, bgcolor: "#E3F2FD", borderRadius: 1.5 }}>
                                        <Typography fontSize={10} fontWeight={600} color="#1565C0">Restaurant Note</Typography>
                                        <Typography fontSize={11} color="#333">{order.portal_note}</Typography>
                                      </Box>
                                    )}
                                    {order.notes && (
                                      <Box sx={{ mb: 1, p: 1, bgcolor: "#FFF3E0", borderRadius: 1.5 }}>
                                        <Typography fontSize={10} fontWeight={600} color="#E65100">Admin Note</Typography>
                                        <Typography fontSize={11} color="#333">{order.notes}</Typography>
                                      </Box>
                                    )}
                                    <Typography fontSize={11} color="#999">Created: {dayjs(order.created_at).format("MMM D, YYYY h:mm A")}</Typography>
                                    {order.updated_at !== order.created_at && (
                                      <Typography fontSize={11} color="#999">Updated: {dayjs(order.updated_at).format("MMM D, YYYY h:mm A")}</Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </Paper>
          )}
        </Grid>
      </Grid>

{/* ── New Order Dialog ── */}
<Dialog
  open={newOrderOpen}
  onClose={() => { if (!newOrderSubmitting) { setNewOrderOpen(false); setNewOrderStep(1); } }}
  maxWidth="sm" fullWidth
  PaperProps={{ sx: { borderRadius: "16px" } }}
>
  <Box sx={{ p: 3 }}>
    {/* Header */}
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
      <Typography fontWeight={700} fontSize={17}>New Order</Typography>
      <Typography fontSize={12} color="text.secondary">Step {newOrderStep} of 2</Typography>
    </Box>

    {/* Step indicator */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
      {[{ n: 1, label: "Select restaurant" }, { n: 2, label: "Build order" }].map((s, i) => (
        <React.Fragment key={s.n}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700,
              bgcolor: newOrderStep > s.n ? "#e8f5e9" : newOrderStep === s.n ? "#e3f2fd" : "#f5f5f5",
              color: newOrderStep > s.n ? "#2e7d32" : newOrderStep === s.n ? "#1565c0" : "#aaa",
            }}>
              {newOrderStep > s.n ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : s.n}
            </Box>
            <Typography fontSize={12} color={newOrderStep === s.n ? "#1565c0" : "text.secondary"} fontWeight={newOrderStep === s.n ? 600 : 400}>
              {s.label}
            </Typography>
          </Box>
          {i === 0 && <Box sx={{ flex: 1, height: "1px", bgcolor: "#e0e0e0" }} />}
        </React.Fragment>
      ))}
    </Box>

    {/* ── STEP 1: Select Shop ── */}
    {newOrderStep === 1 && (
      <>
        <TextField
          size="small" fullWidth placeholder="Search restaurant..."
          value={newOrderShopSearch}
          onChange={(e) => setNewOrderShopSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 16, color: "#999" }} /></InputAdornment> }}
          sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />
        <Box sx={{ border: "1px solid #e8eaf0", borderRadius: "10px", maxHeight: 300, overflowY: "auto" }}>
          {newOrderShopsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>
          ) : newOrderShops.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography fontSize={13} color="text.secondary">No restaurants found</Typography>
            </Box>
          ) : (
            newOrderShops.map((shop) => {
              const selected = newOrderSelectedShop?.id === shop.id;
              const initials = shop.name.split(" ").slice(0, 2).map((w: string) => w[0]).join("").toUpperCase();
              return (
                <Box
                  key={shop.id}
                  onClick={() => setNewOrderSelectedShop(shop)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.25,
                    borderBottom: "1px solid #f0f0f5", cursor: "pointer",
                    bgcolor: selected ? "#e3f2fd" : "transparent",
                    "&:last-child": { borderBottom: "none" },
                    "&:hover": { bgcolor: selected ? "#e3f2fd" : "#fafafa" },
                  }}
                >
                  <Box sx={{
                    width: 30, height: 30, borderRadius: "8px", flexShrink: 0,
                    bgcolor: selected ? "#bbdefb" : "#ede7f6", color: selected ? "#1565c0" : "#512da8",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700,
                  }}>
                    {initials}
                  </Box>
                  <Typography fontSize={13} fontWeight={500} sx={{ flex: 1 }}>{shop.name}</Typography>
                  {selected && <CheckCircleIcon sx={{ fontSize: 17, color: "#1565c0", flexShrink: 0 }} />}
                </Box>
              );
            })
          )}
        </Box>
      </>
    )}

    {/* ── STEP 2: Build Order ── */}
    {newOrderStep === 2 && (
      <>
        <Box sx={{ mb: 1.5, px: 1, py: 0.75, bgcolor: "#f3f6fb", borderRadius: "8px", display: "flex", alignItems: "center", gap: 1 }}>
          <Typography fontSize={12} color="text.secondary">Restaurant:</Typography>
          <Typography fontSize={12} fontWeight={600} color="#0B1F3A">{newOrderSelectedShop?.name}</Typography>
          <Button size="small" onClick={() => { setNewOrderStep(1); setNewOrderCart({}); }}
            sx={{ ml: "auto", fontSize: 11, textTransform: "none", color: "#1565c0", minWidth: 0, p: "2px 6px" }}>
            Change
          </Button>
        </Box>
        {newOrderProductsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>
        ) : newOrderProducts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, bgcolor: "#fafafa", borderRadius: "8px", mb: 2 }}>
            <Typography fontSize={13} color="text.secondary">No products assigned to this restaurant</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 300, mb: 2, border: "1px solid #e8eaf0", borderRadius: "10px" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textAlign: "center" }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textAlign: "center" }}>Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textAlign: "right" }}>Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {newOrderProducts.map((p) => {
                  const pid = p.product_id ?? p.id;
                  const price = p.custom_price != null ? Number(p.custom_price) : Number(p.wholesaleprice ?? p.productPrice ?? 0);
                  const qty = newOrderCart[pid] || 0;
                  return (
                    <TableRow key={pid}>
                      <TableCell sx={{ fontSize: 12, fontWeight: 500 }}>{p.productName || p.name}</TableCell>
                      <TableCell sx={{ fontSize: 12, textAlign: "center", color: "#1565c0", fontWeight: 600 }}>
                        ${price.toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <Button size="small"
                            onClick={() => setNewOrderCart(c => ({ ...c, [pid]: Math.max(0, (c[pid] || 0) - 1) }))}
                            sx={{ minWidth: 24, width: 24, height: 24, p: 0, borderRadius: "6px", bgcolor: "#f5f5f5", color: "#555", "&:hover": { bgcolor: "#e0e0e0" } }}>–</Button>
                          <Typography fontSize={13} fontWeight={600} sx={{ width: 28, textAlign: "center" }}>{qty}</Typography>
                          <Button size="small"
                            onClick={() => setNewOrderCart(c => ({ ...c, [pid]: (c[pid] || 0) + 1 }))}
                            sx={{ minWidth: 24, width: 24, height: 24, p: 0, borderRadius: "6px", bgcolor: "#e8eaf6", color: "#3949ab", "&:hover": { bgcolor: "#c5cae9" } }}>+</Button>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, textAlign: "right", fontWeight: qty > 0 ? 700 : 400, color: qty > 0 ? "#2e7d32" : "text.disabled" }}>
                        {qty > 0 ? `$${(price * qty).toFixed(2)}` : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 1, mb: 1.5 }}>
          <Typography fontWeight={700} fontSize={13}>Total</Typography>
          <Typography fontWeight={800} fontSize={13} color="#2e7d32">
            ${newOrderProducts.reduce((s, p) => {
              const pid = p.product_id ?? p.id;
              const price = p.custom_price != null ? Number(p.custom_price) : Number(p.wholesaleprice ?? p.productPrice ?? 0);
              return s + price * (newOrderCart[pid] || 0);
            }, 0).toFixed(2)}
          </Typography>
        </Box>
        <TextField size="small" fullWidth multiline rows={2} label="Order notes (optional)"
          value={newOrderNotes} onChange={(e) => setNewOrderNotes(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />
      </>
    )}

    {/* Footer */}
    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2.5 }}>
      <Button variant="outlined" onClick={() => { setNewOrderOpen(false); setNewOrderStep(1); }}
        sx={{ borderRadius: "8px", textTransform: "none", borderColor: "#ccc", color: "#666" }}>
        Cancel
      </Button>
      {newOrderStep === 1 ? (
        <Button variant="contained" disabled={!newOrderSelectedShop} onClick={handleNewOrderNext}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#0B1F3A", "&:hover": { bgcolor: "#0F2A4A" } }}>
          Next →
        </Button>
      ) : (
        <Button variant="contained"
          disabled={newOrderSubmitting || !newOrderProducts.some((p) => (newOrderCart[p.product_id ?? p.id] || 0) > 0)}
          onClick={handleNewOrderSubmit}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}>
          {newOrderSubmitting ? "Saving..." : "Submit Order"}
        </Button>
      )}
    </Box>
  </Box>
</Dialog>
      
    </PremiumPage>

    
  );
};

export default ShopOrderList;
